import {
  changeFileToJPG,
  readFilesAndExtractURI,
} from "./ImageConvertResizer";

export const MAX_TICKET_IMAGE_BYTES = 15 * 1024 * 1024;
export const MAX_TICKET_IMAGE_EDGE = 1024;
export const TICKET_IMAGE_JPEG_QUALITY = 90;
// Base64 expands the request by roughly one third. Keep the processed image
// below 4 MiB so the complete API Gateway/Lambda event remains under 6 MiB.
export const MAX_TICKET_UPLOAD_BYTES = 4 * 1024 * 1024;

const SUPPORTED_IMAGE_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/heic",
  "image/heif",
]);

const SUPPORTED_IMAGE_EXTENSIONS = new Set([
  "jpg",
  "jpeg",
  "png",
  "heic",
  "heif",
]);

export class TicketImageError extends Error {
  constructor(code, message) {
    super(message);
    this.name = "TicketImageError";
    this.code = code;
  }
}

const getFileExtension = (fileName = "") =>
  fileName.split(".").pop()?.toLowerCase() || "";

export const isSupportedTicketImage = (file) => {
  if (!file) {
    return false;
  }

  const type = (file.type || "").toLowerCase();
  return (
    SUPPORTED_IMAGE_TYPES.has(type) ||
    SUPPORTED_IMAGE_EXTENSIONS.has(getFileExtension(file.name))
  );
};

export const stripImageDataUrlPrefix = (dataUrl = "") =>
  dataUrl.replace(/^data:image\/[a-zA-Z0-9.+-]+;base64,/, "");

const getBase64ByteLength = (dataUrl) => {
  const base64 = stripImageDataUrlPrefix(dataUrl);
  const padding = (base64.match(/=*$/) || [""])[0].length;
  return Math.max(0, Math.ceil((base64.length * 3) / 4) - padding);
};

const normalizeHeicMimeType = (file) => {
  const extension = getFileExtension(file.name);
  if (
    (extension === "heic" || extension === "heif") &&
    file.type !== "image/heic"
  ) {
    return new File([file], file.name, {
      type: "image/heic",
      lastModified: file.lastModified,
    });
  }

  return file;
};

export const prepareTicketImage = async (file) => {
  if (!isSupportedTicketImage(file)) {
    throw new TicketImageError(
      "UNSUPPORTED_FORMAT",
      "JPG, PNG, HEIC 또는 HEIF 이미지를 선택해 주세요."
    );
  }

  if (file.size > MAX_TICKET_IMAGE_BYTES) {
    throw new TicketImageError(
      "SOURCE_TOO_LARGE",
      "선택할 원본 이미지는 15MB 이하로 준비해 주세요. 전송 전 자동 최적화됩니다."
    );
  }

  try {
    const normalizedFile = normalizeHeicMimeType(file);
    const jpgFile = await changeFileToJPG(normalizedFile);
    const dataUrl = await readFilesAndExtractURI(jpgFile, {
      maxWidth: MAX_TICKET_IMAGE_EDGE,
      maxHeight: MAX_TICKET_IMAGE_EDGE,
      quality: TICKET_IMAGE_JPEG_QUALITY,
    });

    if (getBase64ByteLength(dataUrl) > MAX_TICKET_UPLOAD_BYTES) {
      throw new TicketImageError(
        "PROCESSED_TOO_LARGE",
        "이미지를 처리했지만 전송 크기가 큽니다. 더 작은 이미지를 선택해 주세요."
      );
    }

    return {
      dataUrl,
      fileName: file.name,
      sourceSize: file.size,
    };
  } catch (error) {
    if (error instanceof TicketImageError) {
      throw error;
    }

    throw new TicketImageError(
      "PROCESSING_FAILED",
      "이미지를 처리하지 못했습니다. 다른 이미지로 다시 시도해 주세요."
    );
  }
};
